/**
 * Comprehensive Therapeutic Methods Knowledge Base
 * Contains evidence-based therapy methods for world-class therapeutic intervention
 */

export const therapeuticMethods = {
  // === ANXIETY AND OVERTHINKING ===
  'cbt-anxiety': {
    methodName: 'Cognitive Behavioral Therapy (CBT) for Anxiety',
    description: 'CBT helps identify and challenge anxious thoughts while developing healthier thinking patterns and behaviors.',
    keyPrinciples: [
      'Thoughts, feelings, and behaviors are interconnected',
      'Anxious thoughts are often unrealistic or exaggerated',
      'Changing thought patterns can reduce anxiety symptoms',
      'Gradual exposure builds confidence and reduces avoidance'
    ],
    stepByStepGuide: [
      'Notice when anxiety starts - what triggers it?',
      'Identify the specific anxious thoughts',
      'Ask: "Is this thought realistic? What evidence supports/contradicts it?"',
      'Replace with a more balanced, realistic thought',
      'Take a small action that contradicts the anxious belief',
      'Practice this cycle regularly to build new neural pathways'
    ],
    exampleExercise: 'Thought Record: Write down an anxious thought, rate your anxiety 1-10, list evidence for/against the thought, create a balanced alternative thought, then re-rate your anxiety level.',
    symptoms: ['anxiety', 'overthinking', 'worry', 'racing thoughts', 'catastrophizing'],
    category: 'anxiety-overthinking'
  },

  'act-anxiety': {
    methodName: 'Acceptance and Commitment Therapy (ACT) for Anxiety',
    description: 'ACT teaches you to accept anxious feelings without fighting them while committing to actions aligned with your values.',
    keyPrinciples: [
      'Anxiety is a normal human experience - fighting it makes it worse',
      'You can take meaningful action even while feeling anxious',
      'Values guide behavior more effectively than emotions',
      'Mindful awareness creates space between you and your thoughts'
    ],
    stepByStepGuide: [
      'Notice anxious thoughts and feelings without judgment',
      'Practice the "observer self" - watch your anxiety like clouds passing',
      'Identify what matters most to you (your values)',
      'Ask: "What would I do right now if anxiety wasn\'t controlling me?"',
      'Take one small step toward your values, carrying anxiety with you',
      'Repeat: anxiety can come along, but values drive the bus'
    ],
    exampleExercise: 'Values Compass: List your top 3 life values. When anxiety hits, ask: "How can I honor one of my values right now, even feeling anxious?" Take that action.',
    symptoms: ['anxiety', 'avoidance', 'overthinking', 'worry', 'paralysis'],
    category: 'anxiety-overthinking'
  },

  'mbsr-anxiety': {
    methodName: 'Mindfulness-Based Stress Reduction (MBSR)',
    description: 'MBSR uses mindfulness meditation and body awareness to reduce anxiety and break the cycle of worry.',
    keyPrinciples: [
      'Present-moment awareness reduces future-focused anxiety',
      'Observing thoughts without believing them reduces their power',
      'Body awareness helps release physical tension',
      'Regular practice rewires the brain for calm'
    ],
    stepByStepGuide: [
      'Set aside 10-20 minutes for mindfulness practice',
      'Sit comfortably and focus on your breath',
      'When thoughts arise, acknowledge them: "thinking"',
      'Gently return attention to breathing',
      'Notice body sensations without trying to change them',
      'End by setting an intention for your day'
    ],
    exampleExercise: '4-7-8 Breathing: Inhale for 4 counts, hold for 7, exhale for 8. Repeat 4 times. This activates your parasympathetic nervous system and reduces anxiety immediately.',
    symptoms: ['anxiety', 'stress', 'overthinking', 'tension', 'restlessness'],
    category: 'anxiety-overthinking'
  },

  'exposure-therapy': {
    methodName: 'Exposure Therapy',
    description: 'Gradual, controlled exposure to feared situations helps reduce anxiety and build confidence through experience.',
    keyPrinciples: [
      'Avoidance maintains fear - facing fears reduces them',
      'Start small and build gradually',
      'Anxiety naturally decreases with repeated exposure',
      'Success builds confidence for bigger challenges'
    ],
    stepByStepGuide: [
      'Identify your specific fear or anxiety trigger',
      'Create a "fear ladder" from least to most anxiety-provoking',
      'Start with the lowest rung you can manage',
      'Stay in the situation until anxiety naturally decreases (don\'t escape)',
      'Celebrate each success, no matter how small',
      'Gradually work up the ladder at your own pace'
    ],
    exampleExercise: 'Social Anxiety Ladder: Start with making eye contact with a cashier, then asking a question, then making small talk, building up to bigger social interactions.',
    symptoms: ['phobias', 'social anxiety', 'avoidance', 'panic', 'specific fears'],
    category: 'anxiety-overthinking'
  },

  // === DEPRESSION AND SADNESS ===
  'cbt-depression': {
    methodName: 'Cognitive Behavioral Therapy (CBT) for Depression',
    description: 'CBT identifies negative thought patterns that fuel depression and develops more balanced, realistic thinking.',
    keyPrinciples: [
      'Depression creates distorted thinking patterns',
      'Negative thoughts often feel true but aren\'t accurate',
      'Changing thoughts can lift mood and increase motivation',
      'Behavior changes support emotional healing'
    ],
    stepByStepGuide: [
      'Notice when mood drops - what thoughts preceded it?',
      'Identify the negative thought pattern (all-or-nothing, catastrophizing, etc.)',
      'Examine evidence: "Is this thought completely accurate?"',
      'Develop a more balanced, realistic perspective',
      'Take one small positive action',
      'Track mood changes and thought pattern improvements'
    ],
    exampleExercise: 'Daily Thought Challenge: Each evening, write down one negative thought from the day. List evidence against it and create a more balanced version. Notice how this affects your mood.',
    symptoms: ['depression', 'sadness', 'hopelessness', 'negative thinking', 'low mood'],
    category: 'depression-sadness'
  },

  'behavioral-activation': {
    methodName: 'Behavioral Activation',
    description: 'Behavioral Activation combats depression by gradually increasing meaningful, enjoyable activities.',
    keyPrinciples: [
      'Depression thrives on inactivity and isolation',
      'Action comes before motivation - do first, feel better later',
      'Small steps build momentum for bigger changes',
      'Meaningful activities restore sense of purpose and joy'
    ],
    stepByStepGuide: [
      'List activities you used to enjoy or find meaningful',
      'Rate each activity 1-10 for difficulty and importance',
      'Choose one low-difficulty, high-importance activity',
      'Schedule it for a specific time today',
      'Do it regardless of how you feel',
      'Notice any mood improvement, however small'
    ],
    exampleExercise: 'Daily Pleasant Activity: Choose one small enjoyable activity each day (10-minute walk, favorite song, calling a friend). Schedule it and do it, even if motivation is low.',
    symptoms: ['depression', 'inactivity', 'isolation', 'low motivation', 'apathy'],
    category: 'depression-sadness'
  },

  'ipt-depression': {
    methodName: 'Interpersonal Therapy (IPT)',
    description: 'IPT focuses on improving relationships and social connections to alleviate depression.',
    keyPrinciples: [
      'Depression often stems from relationship problems',
      'Healthy relationships are protective against depression',
      'Communication skills can be learned and improved',
      'Social support is essential for mental health'
    ],
    stepByStepGuide: [
      'Identify important relationships in your life',
      'Notice which relationships drain vs. energize you',
      'Practice expressing feelings and needs directly',
      'Set healthy boundaries with difficult people',
      'Invest more time in supportive relationships',
      'Seek new connections through shared interests'
    ],
    exampleExercise: 'Relationship Inventory: List your 5 closest relationships. Rate each for support (1-10) and stress (1-10). Plan to spend more time with high-support, low-stress people.',
    symptoms: ['depression', 'loneliness', 'relationship problems', 'social isolation'],
    category: 'depression-sadness'
  },

  // === STRESS AND BURNOUT ===
  'mbct-stress': {
    methodName: 'Mindfulness-Based Cognitive Therapy (MBCT) for Stress',
    description: 'MBCT combines mindfulness practices with cognitive tools to break stress cycles and prevent overwhelm.',
    keyPrinciples: [
      'Stress often comes from mental time travel (past regrets, future worries)',
      'Present-moment awareness interrupts stress spirals',
      'Thoughts are mental events, not facts',
      'Self-compassion reduces stress better than self-criticism'
    ],
    stepByStepGuide: [
      'When stress hits, pause and take three deep breaths',
      'Ask: "What is actually happening right now?" (vs. what you\'re imagining)',
      'Notice physical stress sensations without trying to fix them',
      'Observe worried thoughts like clouds passing in the sky',
      'Choose one small, concrete action you can take right now',
      'Practice self-compassion: treat yourself like a good friend would'
    ],
    exampleExercise: 'STOP Technique: When overwhelmed, STOP what you\'re doing, Take a breath, Observe your thoughts/feelings/body, Proceed with intention rather than reaction.',
    symptoms: ['stress', 'overwhelm', 'burnout', 'racing thoughts', 'tension'],
    category: 'stress-burnout'
  },

  'sfbt-stress': {
    methodName: 'Solution-Focused Brief Therapy (SFBT)',
    description: 'SFBT focuses on solutions and strengths rather than problems, helping you build on what\'s already working.',
    keyPrinciples: [
      'You already have many of the resources you need',
      'Small changes can lead to big improvements',
      'Focus on what\'s working, not what\'s broken',
      'Future goals motivate more than past problems'
    ],
    stepByStepGuide: [
      'Identify times when the problem was less severe or absent',
      'Ask: "What was different during those better times?"',
      'Notice your strengths and coping skills you\'ve already used',
      'Set a small, specific goal for improvement',
      'Take one small step toward that goal today',
      'Build on successes rather than focusing on setbacks'
    ],
    exampleExercise: 'Exception Finding: Think of a recent day when stress was more manageable. What was different? What did you do? How can you recreate one element of that today?',
    symptoms: ['stress', 'overwhelm', 'burnout', 'feeling stuck', 'low energy'],
    category: 'stress-burnout'
  },

  // === RELATIONSHIP PROBLEMS ===
  'gottman-method': {
    methodName: 'Gottman Method Couples Therapy',
    description: 'The Gottman Method builds stronger relationships through improved communication, conflict resolution, and emotional connection.',
    keyPrinciples: [
      'Successful relationships have a 5:1 ratio of positive to negative interactions',
      'The "Four Horsemen" (criticism, contempt, defensiveness, stonewalling) predict relationship failure',
      'Emotional attunement and empathy are learnable skills',
      'Conflict is inevitable - it\'s how you handle it that matters'
    ],
    stepByStepGuide: [
      'Replace criticism with "I" statements about specific behaviors',
      'Show genuine interest in your partner\'s thoughts and feelings',
      'During conflict, take breaks when emotions get too intense',
      'Practice the "softened startup" - begin difficult conversations gently',
      'Look for opportunities to appreciate and admire your partner',
      'Create rituals of connection (daily check-ins, weekly date conversations)'
    ],
    exampleExercise: 'Daily Appreciation: Each day, share one specific thing you appreciate about your partner. Focus on actions, character traits, or efforts they made.',
    symptoms: ['relationship conflict', 'communication problems', 'feeling disconnected', 'frequent arguments'],
    category: 'relationship-problems'
  },

  'eft-couples': {
    methodName: 'Emotionally Focused Therapy (EFT)',
    description: 'EFT helps couples understand the emotional cycles that create distance and develops secure emotional bonds.',
    keyPrinciples: [
      'Negative relationship cycles are the problem, not the people',
      'Underneath anger and criticism are usually hurt and fear',
      'Emotional accessibility and responsiveness create security',
      'Healthy attachment is a basic human need'
    ],
    stepByStepGuide: [
      'Identify your relationship\'s negative cycle (pursue-withdraw, attack-defend)',
      'Look beneath surface emotions to find underlying fears and needs',
      'Share vulnerable emotions instead of reactive ones',
      'Ask for what you need instead of criticizing what\'s wrong',
      'Respond to your partner\'s emotional bids for connection',
      'Practice seeing your partner as your ally, not your enemy'
    ],
    exampleExercise: 'Emotion Beneath Emotion: Next time you feel angry or frustrated with your partner, ask: "What am I really afraid of here? What do I need?" Share that deeper emotion.',
    symptoms: ['relationship distance', 'emotional disconnection', 'conflict patterns', 'feeling unheard'],
    category: 'relationship-problems'
  },

  // === TRAUMA AND PTSD ===
  'emdr-trauma': {
    methodName: 'EMDR (Eye Movement Desensitization and Reprocessing)',
    description: 'EMDR helps process traumatic memories so they lose their emotional charge and stop interfering with daily life.',
    keyPrinciples: [
      'Trauma gets "stuck" in the nervous system and needs proper processing',
      'Bilateral stimulation helps the brain integrate traumatic memories',
      'You can heal from trauma without forgetting what happened',
      'The body holds trauma, so healing involves both mind and body'
    ],
    stepByStepGuide: [
      'Work with a trained EMDR therapist for proper trauma processing',
      'Between sessions, practice grounding techniques when triggered',
      'Use bilateral stimulation (cross-lateral movements) during distress',
      'Create a "safe place" visualization for emotional regulation',
      'Build resources and coping skills before processing trauma',
      'Be patient - trauma healing happens in layers over time'
    ],
    exampleExercise: 'Butterfly Hug: Cross your arms over your chest, hands on opposite shoulders. Gently alternate patting your shoulders while breathing slowly. This bilateral stimulation can be calming during distress.',
    symptoms: ['trauma', 'ptsd', 'flashbacks', 'nightmares', 'emotional numbness', 'hypervigilance'],
    category: 'trauma-ptsd'
  },

  'tf-cbt': {
    methodName: 'Trauma-Focused CBT (TF-CBT)',
    description: 'TF-CBT combines cognitive behavioral techniques with trauma-informed care to process traumatic experiences safely.',
    keyPrinciples: [
      'Trauma creates distorted beliefs about self, others, and the world',
      'Avoidance maintains trauma symptoms',
      'Gradual exposure to trauma memories reduces their power',
      'Building coping skills comes before trauma processing'
    ],
    stepByStepGuide: [
      'Learn grounding and relaxation techniques first',
      'Identify trauma-related thoughts and beliefs',
      'Challenge unhelpful trauma-related thoughts with evidence',
      'Gradually approach trauma memories at your own pace',
      'Develop a trauma narrative that makes sense of your experience',
      'Focus on post-traumatic growth and meaning-making'
    ],
    exampleExercise: '5-4-3-2-1 Grounding: Name 5 things you can see, 4 you can touch, 3 you can hear, 2 you can smell, 1 you can taste. This brings you back to the present moment.',
    symptoms: ['trauma', 'ptsd', 'intrusive thoughts', 'avoidance', 'anxiety', 'depression'],
    category: 'trauma-ptsd'
  },

  // === GRIEF AND LOSS ===
  'grief-counseling': {
    methodName: 'Grief Counseling / Complicated Grief Therapy (CGT)',
    description: 'Grief counseling helps process loss in healthy ways while honoring your relationship with what or who was lost.',
    keyPrinciples: [
      'Grief is love with nowhere to go - it\'s a natural response to loss',
      'There\'s no "right" timeline for grief',
      'Grief comes in waves and that\'s normal',
      'You can maintain connection to what you\'ve lost while moving forward'
    ],
    stepByStepGuide: [
      'Allow yourself to feel grief without judgment',
      'Create rituals to honor what you\'ve lost',
      'Share memories and stories with supportive people',
      'Take care of your physical health during grief',
      'Seek professional help if grief feels stuck or overwhelming',
      'Find ways to carry your loved one\'s memory forward'
    ],
    exampleExercise: 'Memory Journal: Write a letter to your loved one or create a memory book. Include favorite stories, lessons they taught you, and how you want to honor their memory.',
    symptoms: ['grief', 'loss', 'sadness', 'yearning', 'difficulty accepting death'],
    category: 'grief-loss'
  },

  'narrative-therapy-grief': {
    methodName: 'Narrative Therapy for Grief',
    description: 'Narrative therapy helps you re-author your story to include loss while identifying ongoing sources of strength and meaning.',
    keyPrinciples: [
      'You are not your grief - you are a person experiencing grief',
      'Loss changes your story but doesn\'t end it',
      'You have agency in how you respond to loss',
      'Meaning can be found even in painful experiences'
    ],
    stepByStepGuide: [
      'Tell your story - before loss, during loss, and hopes for after',
      'Identify values and strengths that persist through grief',
      'Notice moments when grief doesn\'t completely dominate',
      'Ask: "What would your loved one want for your life moving forward?"',
      'Find ways to author a new chapter that honors both loss and growth',
      'Connect with others who share similar experiences'
    ],
    exampleExercise: 'Values Inheritance: List 3 values or qualities your loved one embodied. How can you carry these forward in your own life as a way of honoring them?',
    symptoms: ['grief', 'loss', 'identity confusion', 'feeling stuck', 'searching for meaning'],
    category: 'grief-loss'
  },

  // === LOW SELF-ESTEEM ===
  'cbt-self-esteem': {
    methodName: 'CBT for Self-Esteem',
    description: 'CBT identifies and challenges negative self-beliefs while building evidence for a more balanced self-view.',
    keyPrinciples: [
      'Low self-esteem is maintained by biased thinking patterns',
      'You notice evidence that confirms negative self-beliefs while ignoring positive evidence',
      'Self-worth is not dependent on achievements or others\' approval',
      'Balanced self-evaluation includes both strengths and areas for growth'
    ],
    stepByStepGuide: [
      'Notice your inner critic and what it says',
      'Ask: "Would I talk to a friend this way?"',
      'Look for evidence that contradicts negative self-beliefs',
      'Practice self-compassion instead of self-criticism',
      'Set small, achievable goals to build confidence',
      'Celebrate progress, no matter how small'
    ],
    exampleExercise: 'Evidence Journal: For one week, write down evidence that contradicts your most common self-critical thought. Include compliments received, goals achieved, and kind acts you\'ve done.',
    symptoms: ['low self-esteem', 'self-criticism', 'perfectionism', 'comparing to others'],
    category: 'self-esteem'
  },

  'schema-therapy': {
    methodName: 'Schema Therapy',
    description: 'Schema therapy addresses deep-rooted patterns and beliefs about yourself that formed in childhood and continue to affect you today.',
    keyPrinciples: [
      'Early life experiences create "schemas" - core beliefs about self and world',
      'Schemas feel true but may not be accurate or helpful',
      'You can recognize and change unhelpful schemas',
      'Healing involves meeting unmet emotional needs in healthy ways'
    ],
    stepByStepGuide: [
      'Identify recurring patterns in relationships and self-view',
      'Trace these patterns back to early experiences',
      'Recognize the schema when it\'s activated',
      'Challenge the schema with adult perspective and current evidence',
      'Practice new behaviors that contradict the old schema',
      'Develop a compassionate inner voice to counter the critical one'
    ],
    exampleExercise: 'Schema Dialogue: Write a conversation between your critical inner voice and a compassionate inner voice. Let the compassionate voice offer evidence and support that counters self-criticism.',
    symptoms: ['deep-rooted self-doubt', 'relationship patterns', 'chronic emptiness', 'fear of abandonment'],
    category: 'self-esteem'
  },

  // === ADDICTION AND HABITS ===
  'motivational-interviewing': {
    methodName: 'Motivational Interviewing (MI)',
    description: 'MI helps you explore your own motivations for change rather than being told what you should do.',
    keyPrinciples: [
      'You are the expert on your own life',
      'Ambivalence about change is normal',
      'Intrinsic motivation is more powerful than external pressure',
      'Change happens when benefits outweigh costs'
    ],
    stepByStepGuide: [
      'Explore both sides of your ambivalence about change',
      'List the pros and cons of your current habit/addiction',
      'Identify your personal values and how they relate to change',
      'Set small, specific goals that feel achievable',
      'Plan for obstacles and setbacks',
      'Connect with your deeper "why" for wanting change'
    ],
    exampleExercise: 'Change Talk: Complete these sentences: "I want to change because..." "If I don\'t change..." "If I do change..." "The most important reason to change is..."',
    symptoms: ['addiction', 'unhealthy habits', 'ambivalence about change', 'lack of motivation'],
    category: 'addiction-habits'
  },

  'cbt-addiction': {
    methodName: 'CBT for Addiction',
    description: 'CBT for addiction focuses on identifying triggers, changing thought patterns, and developing healthy coping skills.',
    keyPrinciples: [
      'Addiction is maintained by specific triggers and thought patterns',
      'Cravings are temporary and will pass',
      'You can learn healthier ways to cope with difficult emotions',
      'Relapse is often part of recovery, not a failure'
    ],
    stepByStepGuide: [
      'Identify your specific triggers (people, places, emotions, thoughts)',
      'Develop a plan for each type of trigger',
      'Practice urge surfing - riding out cravings without acting',
      'Build a toolkit of healthy coping strategies',
      'Challenge addiction-supporting thoughts ("I need this to cope")',
      'Create a support network for accountability and encouragement'
    ],
    exampleExercise: 'Trigger Plan: Identify your top 3 addiction triggers. For each one, write 2 specific healthy coping strategies you can use instead of your addictive behavior.',
    symptoms: ['substance abuse', 'behavioral addictions', 'cravings', 'loss of control'],
    category: 'addiction-habits'
  },

  // === LIFE TRANSITIONS ===
  'sfbt-transitions': {
    methodName: 'Solution-Focused Brief Therapy (SFBT) for Transitions',
    description: 'SFBT helps navigate life changes by focusing on strengths, resources, and small steps toward your preferred future.',
    keyPrinciples: [
      'You have successfully navigated changes before',
      'Small changes can create big shifts',
      'Focus on what you want, not what you want to avoid',
      'Your strengths and resources are available to help you now'
    ],
    stepByStepGuide: [
      'Envision your preferred future after this transition',
      'Identify strengths and resources you\'ve used in past changes',
      'Break down the transition into smaller, manageable steps',
      'Notice signs that you\'re already moving in the right direction',
      'Celebrate small wins along the way',
      'Adjust your approach based on what\'s working'
    ],
    exampleExercise: 'Future Self Visualization: Imagine yourself one year from now, having successfully navigated this transition. What does that look like? What small step can you take today toward that vision?',
    symptoms: ['major life changes', 'uncertainty', 'feeling overwhelmed by options', 'fear of unknown'],
    category: 'life-transitions'
  },

  'existential-therapy': {
    methodName: 'Existential Therapy',
    description: 'Existential therapy helps you find meaning and purpose during times of uncertainty and major life changes.',
    keyPrinciples: [
      'Life transitions often trigger questions about meaning and purpose',
      'You have the freedom and responsibility to create your own meaning',
      'Anxiety about uncertainty is a normal part of being human',
      'Authentic living aligns with your deepest values and desires'
    ],
    stepByStepGuide: [
      'Reflect on what gives your life meaning and purpose',
      'Identify your core values and how they guide decisions',
      'Accept uncertainty as a natural part of life',
      'Make choices based on authenticity rather than others\' expectations',
      'Find ways to contribute to something larger than yourself',
      'Practice being present with life as it is, not as you wish it were'
    ],
    exampleExercise: 'Values Clarification: List your top 5 life values. For each, write how you can honor that value during this transition period, regardless of external circumstances.',
    symptoms: ['existential anxiety', 'questioning life direction', 'feeling lost', 'searching for meaning'],
    category: 'life-transitions'
  },

  // === FEELING "OFF" OR DISCONNECTED ===
  'humanistic-therapy': {
    methodName: 'Humanistic/Client-Centered Therapy',
    description: 'Humanistic therapy provides a supportive space for self-exploration and reconnecting with your authentic self.',
    keyPrinciples: [
      'You have an innate drive toward growth and self-actualization',
      'Unconditional positive regard helps you accept all parts of yourself',
      'Your subjective experience is valid and important',
      'Growth happens in relationships characterized by empathy and genuineness'
    ],
    stepByStepGuide: [
      'Create space for honest self-reflection without judgment',
      'Notice what you\'re feeling without trying to fix or change it',
      'Explore what might be contributing to feeling disconnected',
      'Identify moments when you feel most like yourself',
      'Practice self-acceptance for all parts of your experience',
      'Seek relationships where you can be authentic'
    ],
    exampleExercise: 'Authentic Self Check-in: Set aside 10 minutes daily to ask: "How am I really feeling right now? What do I need? What would feel most authentic for me today?"',
    symptoms: ['feeling disconnected', 'emotional numbness', 'questioning identity', 'feeling "off"'],
    category: 'emotional-disconnection'
  },

  'gestalt-therapy': {
    methodName: 'Gestalt Therapy',
    description: 'Gestalt therapy focuses on present-moment awareness and integrating different aspects of yourself.',
    keyPrinciples: [
      'Awareness in the present moment is healing',
      'You are greater than the sum of your parts',
      'Unfinished emotional business from the past affects the present',
      'Integration of all aspects of self leads to wholeness'
    ],
    stepByStepGuide: [
      'Practice present-moment awareness throughout the day',
      'Notice what you\'re experiencing in your body right now',
      'Pay attention to what you\'re avoiding or pushing away',
      'Explore different parts of yourself without judgment',
      'Express emotions and needs directly rather than indirectly',
      'Work to integrate opposing aspects of your personality'
    ],
    exampleExercise: 'Two-Chair Technique: Imagine two parts of yourself in conversation (e.g., the part that wants change vs. the part that resists). Have them dialogue about your current situation.',
    symptoms: ['internal conflict', 'feeling fragmented', 'difficulty expressing emotions', 'feeling stuck'],
    category: 'emotional-disconnection'
  },

  // === CRISIS INTERVENTION ===
  'crisis-intervention': {
    methodName: 'Crisis Intervention and Safety Planning',
    description: 'Immediate crisis intervention focuses on safety, stabilization, and connecting to professional resources.',
    keyPrinciples: [
      'Safety is the absolute first priority',
      'Crisis is temporary - feelings and situations can change',
      'Professional help is available 24/7',
      'You are not alone - support exists',
      'Small steps toward safety are significant victories'
    ],
    stepByStepGuide: [
      'Assess immediate safety - are you in danger right now?',
      'Reach out to crisis professionals immediately if needed',
      'Use grounding techniques to stay present (5-4-3-2-1 method)',
      'Remove or secure any means of self-harm',
      'Call a trusted person or crisis line',
      'Create a safety plan with specific steps and contacts'
    ],
    exampleExercise: 'Safety Plan Creation: Write down 5 people you can call, 3 places you feel safe, and 3 activities that help you feel grounded. Keep this list accessible.',
    symptoms: ['suicidal thoughts', 'self-harm urges', 'crisis', 'emergency', 'overwhelming distress'],
    category: 'crisis-intervention'
  },

  'somatic-experiencing': {
    methodName: 'Somatic Experiencing for Trauma',
    description: 'Somatic Experiencing helps release trapped trauma energy from the body through gentle awareness and movement.',
    keyPrinciples: [
      'Trauma is stored in the body, not just the mind',
      'The nervous system can be gently guided back to regulation',
      'Small movements and sensations can create big healing shifts',
      'Your body has natural wisdom for healing'
    ],
    stepByStepGuide: [
      'Notice where you feel tension or discomfort in your body',
      'Breathe gently and send breath to that area',
      'Make small, slow movements (shoulder rolls, gentle stretching)',
      'Notice any changes in sensation without forcing anything',
      'Allow natural movements or positions that feel good',
      'End by placing hands on heart and taking three deep breaths'
    ],
    exampleExercise: 'Body Scan Check-in: Starting from your head, slowly scan down your body. Notice areas of tension without judgment. Breathe into those areas and see if they naturally want to move or stretch.',
    symptoms: ['trauma', 'ptsd', 'anxiety', 'panic', 'physical tension', 'hypervigilance'],
    category: 'trauma-ptsd'
  },

  // === DBT SKILLS ===
  'dbt-skills': {
    methodName: 'Dialectical Behavior Therapy (DBT) Skills',
    description: 'DBT teaches four core skills modules for managing intense emotions and building a life worth living.',
    keyPrinciples: [
      'Dialectical thinking - holding two seemingly opposite truths at once',
      'Distress tolerance - surviving crisis without making it worse',
      'Emotion regulation - understanding and managing intense feelings',
      'Interpersonal effectiveness - getting needs met while maintaining relationships'
    ],
    stepByStepGuide: [
      'Learn mindfulness as the foundation skill',
      'Practice distress tolerance during crisis moments',
      'Identify and name emotions as they arise',
      'Use PLEASE skills for emotion regulation (treat illness, balance eating, avoid substances, get sleep, exercise)',
      'Apply DEAR MAN for interpersonal requests',
      'Practice radical acceptance for things you cannot change'
    ],
    exampleExercise: 'TIPP for Crisis Survival: Temperature change (cold water on face), Intense exercise (run/jumping jacks), Paced breathing (slow exhale longer than inhale), Paired muscle relaxation.',
    symptoms: ['emotional intensity', 'self-harm urges', 'relationship problems', 'impulsivity', 'identity issues'],
    category: 'emotional-regulation'
  }
};

export const symptomToMethodsMap = {
  // Anxiety and Overthinking
  'anxiety': ['cbt-anxiety', 'act-anxiety', 'mbsr-anxiety', 'exposure-therapy'],
  'overthinking': ['cbt-anxiety', 'act-anxiety', 'mbsr-anxiety'],
  'worry': ['cbt-anxiety', 'act-anxiety', 'mbsr-anxiety'],
  'racing thoughts': ['cbt-anxiety', 'mbsr-anxiety'],
  'catastrophizing': ['cbt-anxiety'],
  'phobias': ['exposure-therapy'],
  'social anxiety': ['exposure-therapy', 'cbt-anxiety'],
  'panic': ['exposure-therapy', 'cbt-anxiety'],
  'avoidance': ['act-anxiety', 'exposure-therapy'],
  'paralysis': ['act-anxiety'],

  // Depression and Sadness
  'depression': ['cbt-depression', 'behavioral-activation', 'ipt-depression'],
  'sadness': ['cbt-depression', 'behavioral-activation', 'ipt-depression'],
  'hopelessness': ['cbt-depression', 'behavioral-activation'],
  'low mood': ['cbt-depression', 'behavioral-activation'],
  'negative thinking': ['cbt-depression'],
  'inactivity': ['behavioral-activation'],
  'isolation': ['behavioral-activation', 'ipt-depression'],
  'low motivation': ['behavioral-activation'],
  'apathy': ['behavioral-activation'],
  'loneliness': ['ipt-depression'],

  // Stress and Burnout
  'stress': ['mbct-stress', 'sfbt-stress', 'act-anxiety'],
  'overwhelm': ['mbct-stress', 'sfbt-stress'],
  'burnout': ['mbct-stress', 'sfbt-stress'],
  'tension': ['mbsr-anxiety', 'mbct-stress'],
  'feeling stuck': ['sfbt-stress', 'sfbt-transitions'],
  'low energy': ['sfbt-stress', 'behavioral-activation'],
  'restlessness': ['mbsr-anxiety'],

  // Relationship Problems
  'relationship conflict': ['gottman-method'],
  'communication problems': ['gottman-method', 'eft-couples'],
  'feeling disconnected': ['eft-couples', 'humanistic-therapy'],
  'frequent arguments': ['gottman-method'],
  'relationship distance': ['eft-couples'],
  'emotional disconnection': ['eft-couples', 'humanistic-therapy'],
  'conflict patterns': ['eft-couples'],
  'feeling unheard': ['eft-couples'],

  // Trauma and PTSD
  'trauma': ['emdr-trauma', 'tf-cbt', 'somatic-experiencing'],
  'ptsd': ['emdr-trauma', 'tf-cbt'],
  'flashbacks': ['emdr-trauma', 'tf-cbt'],
  'nightmares': ['emdr-trauma'],
  'emotional numbness': ['emdr-trauma', 'humanistic-therapy'],
  'hypervigilance': ['emdr-trauma', 'somatic-experiencing'],
  'intrusive thoughts': ['tf-cbt'],
  'physical tension': ['somatic-experiencing'],

  // Grief and Loss
  'grief': ['grief-counseling', 'narrative-therapy-grief'],
  'loss': ['grief-counseling', 'narrative-therapy-grief'],
  'yearning': ['grief-counseling'],
  'difficulty accepting death': ['grief-counseling'],
  'searching for meaning': ['narrative-therapy-grief', 'existential-therapy'],
  'identity confusion': ['narrative-therapy-grief'],

  // Self-Esteem
  'low self-esteem': ['cbt-self-esteem', 'schema-therapy'],
  'self-criticism': ['cbt-self-esteem', 'schema-therapy'],
  'perfectionism': ['cbt-self-esteem'],
  'comparing to others': ['cbt-self-esteem'],
  'deep-rooted self-doubt': ['schema-therapy'],
  'fear of abandonment': ['schema-therapy'],
  'relationship patterns': ['schema-therapy'],
  'chronic emptiness': ['schema-therapy'],

  // Addiction and Habits
  'addiction': ['motivational-interviewing', 'cbt-addiction'],
  'unhealthy habits': ['motivational-interviewing', 'cbt-addiction'],
  'substance abuse': ['cbt-addiction'],
  'behavioral addictions': ['cbt-addiction'],
  'cravings': ['cbt-addiction'],
  'loss of control': ['cbt-addiction'],
  'ambivalence about change': ['motivational-interviewing'],
  'lack of motivation': ['motivational-interviewing'],

  // Life Transitions
  'major life changes': ['sfbt-transitions', 'existential-therapy'],
  'uncertainty': ['sfbt-transitions', 'existential-therapy'],
  'fear of unknown': ['sfbt-transitions'],
  'existential anxiety': ['existential-therapy'],
  'questioning life direction': ['existential-therapy'],
  'feeling lost': ['existential-therapy', 'humanistic-therapy'],
  'feeling overwhelmed by options': ['sfbt-transitions'],

  // Emotional Disconnection
  'feeling off': ['humanistic-therapy', 'gestalt-therapy'],
  'questioning identity': ['humanistic-therapy', 'existential-therapy'],
  'internal conflict': ['gestalt-therapy'],
  'feeling fragmented': ['gestalt-therapy'],
  'difficulty expressing emotions': ['gestalt-therapy'],

  // Crisis
  'suicidal thoughts': ['crisis-intervention', 'dbt-skills'],
  'self-harm urges': ['crisis-intervention', 'dbt-skills'],
  'crisis': ['crisis-intervention'],
  'emergency': ['crisis-intervention'],
  'overwhelming distress': ['crisis-intervention', 'dbt-skills'],

  // Emotional Regulation
  'emotional intensity': ['dbt-skills'],
  'impulsivity': ['dbt-skills'],
  'identity issues': ['dbt-skills', 'schema-therapy']
};
